import React, { useState } from "react";
import {
  Table,
  Badge,
  ScrollArea,
  createStyles,
  Avatar,
  Text,
  ActionIcon,
  Group,
  Skeleton,
  Center,
  Stack,
  Tooltip,
  Modal,
  TextInput,
  Button,
  Loader,
} from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useGetDirectorTeamLeaveRequests } from "../../../api/leave/getDirectorTeamLeaveRequests";
import { useUpdateLeaveStatus } from "../../../api/leaveRequest/statusLeaveRequest";

const useStyles = createStyles((theme) => ({
  header: {
    position: "sticky",
    top: 0,
    backgroundColor:
      theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
    transition: "box-shadow 150ms ease",
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      borderBottom: `1px solid ${
        theme.colorScheme === "dark"
          ? theme.colors.dark[3]
          : theme.colors.gray[2]
      }`,
    },
  },
}));

// Helper function to get badge color based on status
const getBadgeColor = (status: string): string => {
  switch (status) {
    case "Approved":
      return "green";
    case "Pending":
      return "orange";
    case "Rejected":
      return "red";
    default:
      return "gray";
  }
};

interface DirectorLeaveTableProps {
  filterType: string;
  dateRange: [Date | null, Date | null];
}

export function DirectorLeaveTable({
  filterType,
  dateRange,
}: DirectorLeaveTableProps) {
  const { classes } = useStyles();
  const [page] = useState(1);
  const [limit] = useState(10);
  const { data: leaveResponse, isLoading } = useGetDirectorTeamLeaveRequests(
    page,
    limit,
    "Pending",
  );
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { mutate: updateLeaveStatus, isLoading: isUpdating } =
    useUpdateLeaveStatus();

  const [selectedLeave, setSelectedLeave] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);

  if (isLoading) {
    return (
      <ScrollArea>
        <Stack spacing="md">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={60} radius="md" />
          ))}
        </Stack>
      </ScrollArea>
    );
  }

  const leaveData = leaveResponse?.data?.requests || [];

  const handleApprove = (leaveId: string) => {
    updateLeaveStatus({
      leaveId,
      status: "approved",
      reviewedByReason: "Approved by director/supervisor",
    });
  };

  const handleReject = () => {
    if (!selectedLeave) return;
    updateLeaveStatus({
      leaveId: selectedLeave,
      status: "rejected",
      reviewedByReason: rejectionReason || "Leave request rejected",
    });
    setShowRejectModal(false);
    setRejectionReason("");
    setSelectedLeave(null);
  };

  const openRejectModal = (leaveId: string) => {
    setSelectedLeave(leaveId);
    setShowRejectModal(true);
  };

  const rows = leaveData.map((row) => (
    <tr key={row.id}>
      <td>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar src={row.avatar} radius="xl" size="sm" />
          <div>
            <Text size="sm" fw={500}>
              {row.userName}
            </Text>
            <Text size="xs" c="dimmed">
              {row.userRole}
            </Text>
          </div>
        </div>
      </td>
      <td>
        <Badge variant="outline">{row.leaveType}</Badge>
      </td>
      <td>
        <Text size="sm">{row.reason}</Text>
      </td>
      <td>{new Date(row.fromDate).toLocaleDateString()}</td>
      <td>{new Date(row.toDate).toLocaleDateString()}</td>
      <td>{row.numberOfDays}</td>
      <td>
        <Badge color={getBadgeColor(row.status)}>{row.status}</Badge>
      </td>
      {/* <td>
        {row.status === "Pending" && (
          <Group spacing="xs">
            <Tooltip label="Approve Leave" withArrow>
              <ActionIcon
                color="green"
                variant="light"
                title="Approve"
                onClick={() => handleApprove(row.id)}
                disabled={isUpdating}
              >
                {isUpdating ? <Loader size="xs" /> : <IconCheck size={16} />}
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Reject Leave" withArrow>
              <ActionIcon
                color="red"
                variant="light"
                title="Reject"
                onClick={() => openRejectModal(row.id)}
                disabled={isUpdating}
              >
                {isUpdating ? <Loader size="xs" /> : <IconX size={16} />}
              </ActionIcon>
            </Tooltip>
          </Group>
        )}
      </td> */}
    </tr>
  ));

  if (leaveData.length === 0) {
    return (
      <Center py="xl">
        <Text c="dimmed">No pending leave requests</Text>
      </Center>
    );
  }

  return (
    <>
      <ScrollArea>
        <Table style={{ minWidth: 800 }} verticalSpacing="sm">
          <thead className={classes.header}>
            <tr>
              <th>Employee</th>
              <th>Type</th>
              <th>Reason</th>
              <th>From</th>
              <th>To</th>
              <th>Days</th>
              <th>Status</th>
              {/* <th>Action</th> */}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </Table>
      </ScrollArea>

      <Modal
        opened={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setRejectionReason("");
          setSelectedLeave(null);
        }}
        title="Reject Leave Request"
        centered
      >
        <Stack spacing="md">
          <Text size="sm" c="dimmed">
            Please provide a reason for rejecting this leave request.
          </Text>
          <TextInput
            label="Rejection Reason"
            placeholder="Enter reason..."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.currentTarget.value)}
            required
          />
          <Group position="right">
            <Button
              variant="default"
              onClick={() => {
                setShowRejectModal(false);
                setRejectionReason("");
                setSelectedLeave(null);
              }}
            >
              Cancel
            </Button>
            {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
            <Button
              color="red"
              onClick={handleReject}
              disabled={!rejectionReason.trim() || isUpdating}
              loading={isUpdating}
            >
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
